import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { useTheme } from '@repo/ui/theming';
import { capitalizeFirstLetter } from '@repo/ui/utils';

import { FREE_TRIAL_DAYS_AMOUNT } from '../../../global/constants';
import useFreeTrial from '../../../hooks/useFreeTrial';
import { Link } from '../SubscriptionSuccessModal/styles';
import { getAddonFromSubscription, getDateLabel } from '../utils';

import {
  SubscriptionSuccessModalData,
  SubscriptionSuccessModalParams,
} from './types';
import usePricingPlansData from './usePricingPlansData';

const usePaymentSuccessModalData = ({
  subscription,
  plan,
  singlePayment,
}: SubscriptionSuccessModalParams): SubscriptionSuccessModalData => {
  const freeTrial = useFreeTrial();

  const openBillingPortal = useSubscriptionStore(
    subscriptionStoreSelector.openBillingPortal
  );

  const { title, mainFeatures, otherFeatures } = usePricingPlansData({
    addon: getAddonFromSubscription(subscription),
  })[plan];

  const { isDarkTheme } = useTheme();

  if (singlePayment) {
    const imageUrl = isDarkTheme
      ? '/images/newTheme/dark/Treasure.png'
      : '/images/newTheme/light/Treasure.png';

    return {
      title: 'Payment Successful',
      description: (
        <>
          Your payment was successful. You can now enjoy all extra features.
          Visit the{' '}
          <Link target="_blank" onClick={openBillingPortal}>
            Payment & Billing
          </Link>{' '}
          page for managing your billing information.
        </>
      ),
      lead: 'Your payment was successful. You can now enjoy all extra features.',
      buttonLabel: 'Start Creating',
      imageUrl,
    };
  }

  if (freeTrial.isRunning) {
    const imageUrl = isDarkTheme
      ? '/images/newTheme/dark/Congrats.png'
      : '/images/newTheme/light/Congrats.png';

    const freeTrialEndDateLabel = getDateLabel(freeTrial.trialEndDate);

    return {
      title: `Welcome to Your ${FREE_TRIAL_DAYS_AMOUNT}-Day Free Trial`,
      description: `Your Free Trial is now active until ${freeTrialEndDateLabel}. You can now enjoy all extra features and premium templates.`,
      badge: `${capitalizeFirstLetter(plan.toLowerCase())} Plan`,
      featuresLabel: "Here is what's included:",
      mainFeatures,
      otherFeatures,
      buttonLabel: 'Got It',
      imageUrl,
    };
  }

  const imageUrl = isDarkTheme
    ? '/images/newTheme/dark/Treasure.png'
    : '/images/newTheme/light/Treasure.png';

  return {
    title: `Welcome to Kittl ${title}`,
    description: (
      <>
        Your Kittl subscription is now active. You can now enjoy all extra
        features. Visit the{' '}
        <Link target="_blank" onClick={openBillingPortal}>
          Payment & Billing
        </Link>{' '}
        page for managing your billing information.
      </>
    ),
    featuresLabel: 'Features Unlocked:',
    mainFeatures,
    otherFeatures,
    buttonLabel: 'Continue',
    imageUrl,
  };
};

export default usePaymentSuccessModalData;
